import { useState, useMemo } from 'react';
import {
  XMarkIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  HomeIcon,
  ArrowPathIcon,
  DocumentTextIcon,
  FlagIcon,
  NoSymbolIcon,
  PhoneIcon,
  UserIcon,
  StarIcon
} from '@heroicons/react/24/outline';

// Default status options if no config is provided
const DEFAULT_STATUS_OPTIONS = [
  { id: 'contacted', label: 'Contacted', color: '#10B981', icon: 'CheckCircleIcon' },
  { id: 'not_home', label: 'Not Home', color: '#F59E0B', icon: 'ClockIcon' },
  { id: 'refused', label: 'Refused', color: '#EF4444', icon: 'XCircleIcon' },
  { id: 'moved', label: 'Moved', color: '#8B5CF6', icon: 'HomeIcon' }
];

// Icon mapping
const ICON_MAP = {
  CheckCircleIcon,
  HomeIcon,
  DocumentTextIcon,
  FlagIcon,
  NoSymbolIcon,
  PhoneIcon,
  UserIcon,
  StarIcon,
  XCircleIcon,
  ClockIcon,
};

function getIconComponent(iconName) {
  return ICON_MAP[iconName] || CheckCircleIcon;
}

function CustomFieldInput({ field, value, onChange }) {
  const handleChange = (newValue) => {
    onChange(field.id, newValue);
  };

  // Support both field.type and field.field_type for API compatibility
  const fieldType = field.field_type || field.type;

  switch (fieldType) {
    case 'text':
      return (
        <input
          type="text"
          value={value || ''}
          onChange={(e) => handleChange(e.target.value)}
          placeholder={field.placeholder || ''}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
        />
      );

    case 'textarea':
      return (
        <textarea
          value={value || ''}
          onChange={(e) => handleChange(e.target.value)}
          placeholder={field.placeholder || ''}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
        />
      );

    case 'select':
      return (
        <select
          value={value || ''}
          onChange={(e) => handleChange(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="">Select...</option>
          {field.options?.map((opt) => (
            <option key={opt.value || opt} value={opt.value || opt}>
              {opt.label || opt}
            </option>
          ))}
        </select>
      );

    case 'checkbox':
      return (
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={value || false}
            onChange={(e) => handleChange(e.target.checked)}
            className="h-4 w-4 text-blue-600 rounded border-gray-300"
          />
          <span className="text-gray-700">{field.label}</span>
        </label>
      );

    case 'radio':
      return (
        <div className="space-y-2">
          {field.options?.map((opt) => (
            <label key={opt.value || opt} className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name={field.id}
                value={opt.value || opt}
                checked={value === (opt.value || opt)}
                onChange={(e) => handleChange(e.target.value)}
                className="h-4 w-4 text-blue-600 border-gray-300"
              />
              <span className="text-gray-700">{opt.label || opt}</span>
            </label>
          ))}
        </div>
      );

    case 'rating':
      const maxRating = field.max || 5;
      return (
        <div className="flex gap-1">
          {[...Array(maxRating)].map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => handleChange(i + 1)}
              className={`w-10 h-10 rounded-lg font-medium transition-colors ${
                value > i
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {i + 1}
            </button>
          ))}
        </div>
      );

    case 'number':
      return (
        <input
          type="number"
          value={value || ''}
          onChange={(e) => handleChange(parseFloat(e.target.value) || null)}
          min={field.min || field.min_value}
          max={field.max || field.max_value}
          step={field.step || 1}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
        />
      );

    case 'date':
      return (
        <input
          type="date"
          value={value || ''}
          onChange={(e) => handleChange(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
        />
      );

    default:
      return (
        <input
          type="text"
          value={value || ''}
          onChange={(e) => handleChange(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
        />
      );
  }
}

export default function VoterCheckIn({
  voter,
  customFields = [],
  canvassingConfig,
  onSubmit,
  onClose,
  isLoading = false
}) {
  // Get status options from config or use defaults
  const statusOptions = useMemo(() => {
    return canvassingConfig?.statusOptions || DEFAULT_STATUS_OPTIONS;
  }, [canvassingConfig]);

  // Get default status (first one marked as default, or first option)
  const defaultStatus = useMemo(() => {
    const defaultOption = statusOptions.find(opt => opt.isDefault);
    return defaultOption?.id || statusOptions[0]?.id || 'contacted';
  }, [statusOptions]);

  const [status, setStatus] = useState(voter?.check_in?.status || defaultStatus);
  const [notes, setNotes] = useState(voter?.check_in?.notes || '');
  const [customValues, setCustomValues] = useState(voter?.check_in?.custom_fields || {});

  const handleCustomFieldChange = (fieldId, value) => {
    setCustomValues(prev => ({
      ...prev,
      [fieldId]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      status,
      notes,
      custom_fields: customValues,
      timestamp: new Date().toISOString()
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white w-full sm:max-w-md sm:rounded-xl rounded-t-xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Check In</h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4 overflow-y-auto max-h-[70vh]">
          {/* Status selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Status
            </label>
            <div className="grid grid-cols-2 gap-2">
              {statusOptions.map((option) => {
                const IconComponent = getIconComponent(option.icon);
                const isSelected = status === option.id;
                return (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => setStatus(option.id)}
                    className={`flex items-center justify-center gap-2 p-3 rounded-lg border-2 transition-colors ${
                      isSelected
                        ? 'border-current'
                        : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                    }`}
                    style={isSelected ? {
                      borderColor: option.color,
                      backgroundColor: `${option.color}15`,
                      color: option.color
                    } : undefined}
                  >
                    <IconComponent className="h-5 w-5" />
                    <span className="font-medium">{option.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notes
              {canvassingConfig?.requireNotes && <span className="text-red-500 ml-1">*</span>}
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add any notes about this interaction..."
              rows={3}
              required={canvassingConfig?.requireNotes}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-blue-500 focus:border-blue-500 ${
                canvassingConfig?.requireNotes && !notes.trim()
                  ? 'border-red-300'
                  : 'border-gray-300'
              }`}
            />
            {canvassingConfig?.requireNotes && !notes.trim() && (
              <p className="mt-1 text-xs text-red-500">Notes are required for this check-in</p>
            )}
          </div>

          {/* Custom fields */}
          {customFields.length > 0 && (
            <div className="border-t border-gray-200 pt-4">
              <h3 className="text-sm font-medium text-gray-700 mb-3">
                Additional Information
              </h3>
              <div className="space-y-4">
                {customFields.map((field) => (
                  <div key={field.id}>
                    {field.type !== 'checkbox' && (
                      <label className="block text-sm font-medium text-gray-600 mb-1">
                        {field.label}
                        {field.required && <span className="text-red-500 ml-1">*</span>}
                      </label>
                    )}
                    <CustomFieldInput
                      field={field}
                      value={customValues[field.id]}
                      onChange={handleCustomFieldChange}
                    />
                    {field.description && (
                      <p className="mt-1 text-xs text-gray-500">{field.description}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </form>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 bg-gray-50">
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              onClick={handleSubmit}
              disabled={isLoading || (canvassingConfig?.requireNotes && !notes.trim())}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50"
            >
              {isLoading ? (
                <>
                  <ArrowPathIcon className="h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Check In'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
